import { UserContext } from 'contexts/user/UserContext';
import { useContext, useEffect, useState } from 'react';
import { useHistory, useParams, Link } from 'react-router-dom';
import IntlMessages from 'helpers/IntlMessages';
import { Card, ListGroup } from 'react-bootstrap';
import { FormattedTime } from 'react-intl';
import DOMPurify from 'dompurify';

const fetchAllCategoriesFlat = async (): Promise<any[]> => {
  const res = await fetch('/api/discourse/categories');
  if (!res.ok) throw new Error(`Failed to fetch categories: ${res.status}`);
  const data = await res.json();
  const all: any[] = [];

  const walk = (cats: any[]) => {
    for (const c of cats) {
      // Push this node
      all.push(c);

      // Recurse into subcategories if present
      if (c.subcategories?.length) {
        walk(c.subcategories);
      }

      // Some Discourse APIs also expose `categories` instead of `subcategories`
      if (c.categories?.length) {
        walk(c.categories);
      }
    }
  };

  walk(data);

  return all;
};

const Forum = () => {
  const { currentUser } = useContext(UserContext);
  const history = useHistory();
  const params = useParams<{ categoryId?: string; topicId?: string }>();
  const [topics, setTopics] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState<any[]>([]);
  const [_parentCategory, setParentCategory] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const categoryId = params.categoryId;
  const topicId = params.topicId;
  const discourseUrl = import.meta.env.VITE_DISCOURSE_URL as string;
  const [breadcrumbPath, setBreadcrumbPath] = useState<Array<{ label: string; url: string }>>([
    { label: 'Forum', url: '/forum' },
  ]);

  // Load all categories on mount
  useEffect(() => {
    const loadAllCategories = async () => {
      try {
        const cats = await fetchAllCategoriesFlat();
        setAllCategories(cats);
      } catch (err) {
        console.error('Failed to load categories', err);
      }
    };

    loadAllCategories();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (allCategories.length === 0) return; // wait for allCategories to be loaded

      setLoading(true);
      try {
        if (topicId) {
          const res = await fetch(`/api/discourse/topic-full/${topicId}`);
          if (!res.ok) throw new Error(`Failed to fetch topic: ${res.status}`);
          const topicData = await res.json();
          setTopics([topicData]);
          setCategories([]);
          setParentCategory(null);

          const topic = topicData as any;

          // Reuse the categoryId from the URL (not topic.category)
          if (categoryId) {
            const category = allCategories.find((c) => c.id.toString() === categoryId);
            if (category) {
              const categoryPath = buildCategoryPath(category);

              setBreadcrumbPath([
                { label: 'Forum', url: '/forum' },
                ...categoryPath,
                { label: topic.fancy_title, url: `/forum/${categoryId}/${topic.id}` },
              ]);
              return;
            }
          }

          // Fallback: if category not found, just Forum / Topic
          setBreadcrumbPath([
            { label: 'Forum', url: '/forum' },
            { label: topic.fancy_title, url: `/forum/${categoryId ?? ''}/${topic.id}` },
          ]);
        } else if (categoryId) {
          const res = await fetch(`/api/discourse/category-tree/${categoryId}`);
          if (!res.ok) throw new Error(`Failed to fetch category tree: ${res.status}`);
          const data = await res.json();
          const cat = data.category;
          setParentCategory(cat);
          setCategories(data.subcategories || []);
          setTopics(data.topics || []);

          const categoryPath = buildCategoryPath(cat);
          setBreadcrumbPath([
            { label: 'Forum', url: '/forum' },
            ...categoryPath,
          ]);
        } else {
          const res = await fetch('/api/discourse/categories');
          if (!res.ok) throw new Error(`Failed to fetch categories: ${res.status}`);
          const data = await res.json();
          const topLevelCategories = data.filter((cat: any) => !cat.parent_category_id);
          setCategories(topLevelCategories);
          setParentCategory(null);

          // Root: just "Forum"
          setBreadcrumbPath([{ label: 'Forum', url: '/forum' }]);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    const buildCategoryPath = (cat: any): Array<{ label: string; url: string }> => {
      const path: Array<{ label: string; url: string }> = [];

      let current = cat;
      while (current) {
        path.unshift({
          label: current.name,
          url: `/forum/${current.id}`,
        });

        const parentId = current.parent_category_id;
        if (!parentId) {
          break;
        }

        const parentCategoryId = typeof parentId === 'string' ? parseInt(parentId, 10) : parentId;
        current = allCategories.find((c: any) => c.id === parentCategoryId);
      }

      return path;
    };

    loadData();
  }, [categoryId, topicId, allCategories]);

  const navigateToCategory = (catId: string) => {
    history.push(`/forum/${catId}`);
  };

  if (!currentUser?.id) return <div><IntlMessages id="general.loading" /></div>;
  if (!discourseUrl) return <div>Forum configuration missing</div>;

  return (
    <Card className='w-100'>
      <Card.Header className="p-3 pb-2">
        {/* Breadcrumb */}
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb mb-2 p-0 bg-transparent">
            {breadcrumbPath.map((item, index) => (
              <li
                key={item.url}
                className={`breadcrumb-item ${index === breadcrumbPath.length - 1 ? 'active' : ''}`}
              >
                {index === breadcrumbPath.length - 1 ? (
                  <span>{item.label}</span>
                ) : (
                  <Link to={item.url} >
                    {item.label}
                  </Link>
                )}
              </li>
            ))}
          </ol>
        </nav>

      </Card.Header>

      <Card.Body className="p-0 d-flex flex-column" style={{ height: 'calc(100% - 60px)' }}>
        {loading ? (
          <div className="p-4 text-center text-muted">
            <IntlMessages id="general.loading" />
          </div>
        ) : topicId ? (
          // Show specific topic
          topics.length > 0 ? (
            <div className="p-4">
              <div className="mb-3">
                <Link to={categoryId ? `/forum/${categoryId}` : '/forum'} className="btn btn-outline-secondary btn-sm mb-3">
                  <IntlMessages id="general.back" />
                </Link>
                <h1 className="h3 m-3 fw-bold">{(topics[0] as any)?.fancy_title}</h1>
              </div>

              {/* Post content */}
              {(topics[0] as any)?.firstPost ? (
                <div className="topic-content">
                <div
                  className="post-content cooked"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize((topics[0] as any).firstPost.cooked || '')
                  }}
                />
                <div className="mt-3">
                  <small className="text-muted">
                    Posted by <strong>{(topics[0] as any).firstPost.username}</strong> -
                    {new Date((topics[0] as any).firstPost.created_at).toLocaleString()}
                  </small>
                </div>
                </div>
              ) : (
                <div className="alert alert-info">No content available</div>
              )}

              {/* Replies */}
              {(topics[0] as any)?.posts && (topics[0] as any).posts.length > 1 && (
                <div className="mt-4">
                  <h6>Replies ({(topics[0] as any).posts.length - 1})</h6>
                  {(topics[0] as any).posts.slice(1).map((post: any) => (
                    <div key={post.id} className="border-top pt-3 mt-3">
                    <div
                      className="post-content cooked mb-2"
                      dangerouslySetInnerHTML={{ __html: post.cooked || '' }}
                    />
                    <small className="text-muted">
                      {post.username} • {new Date(post.created_at).toLocaleString()}
                    </small>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="p-4 text-center text-muted">Topic not found</div>
          )

        ) : categoryId ? (
          // Show category: Subcategories + topics
          <>
            {categories.length > 0 && (
              <div className="p-3 border-bottom bg-light">
                <div className="row">
                  {categories.slice(0, 6).map((category: any) => (
                    <div key={category.id} className="col-md-6 col-lg-4 mb-3">
                      <button
                        onClick={() => navigateToCategory(category.id.toString())}
                        className="card h-100 border-0 shadow-sm hover-lift text-decoration-none text-start bg-white p-3 rounded"
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h6 className="mb-1 fw-semibold text-truncate">{category.name}</h6>
                          {/* {category.topic_count > 0 && (
                              <Badge variant="primary" className="ms-2">{category.topic_count}</Badge>
                              )} */}
                        </div>
                        {/* <small className="text-muted">{category.topic_count} posts</small> */}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Topics section */}
            <div className="p-3 flex-grow-1 d-flex flex-column" style={{ overflowY: 'auto' }}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Topics ({topics.length})</h6>
              </div>
              {topics.length === 0 ? (
                <div className="text-muted">No topics yet</div>
              ) : (
                <ListGroup variant="flush">
                  {topics.slice(0, 50).map((topic: any) => (
                    <ListGroup.Item
                      key={topic.id}
                      action
                      className="p-2 border-0"
                      as={Link}
                      to={`/forum/${categoryId}/${topic.id}`}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <h6 className="mb-1 fw-normal lh-sm">{topic.title}</h6>
                          <small className="text-muted">
                            <IntlMessages id="forum.latest-reply" /> : &nbsp;
                            <FormattedTime value={topic.created_at} year="numeric" month="short" day="2-digit" /> - {topic.last_poster_username || topic.username}
                          </small>
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </div>
          </>
        ) : (
          // Root: Top-level categories
          <div className="p-3 flex-grow-1 d-flex flex-column" style={{ overflowY: 'auto' }}>
            {categories.length === 0 ? (
              <div className="text-muted">No categories available</div>
            ) : (
              <div className="row">
                {categories.map((category: any) => (
                  <div key={category.id} className="col-md-6 col-lg-4 mb-3">
                    <Link
                      to={`/forum/${category.id}`}
                      className="text-decoration-none"
                    >
                      <Card className="h-100 border-0 shadow-sm hover-lift">
                        <Card.Body className="p-3">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="mb-1 fw-semibold text-truncate" style={{ maxHeight: '40px' }}>
                              {category.name}
                            </h6>
                            {/* {category.topic_count > 0 && (
                                <Badge variant="primary" className="ms-2">{category.topic_count}</Badge>
                                )} */}
                          </div>
                          {category.description && (
                            <p className="small text-muted mb-2" style={{ maxHeight: '60px', overflow: 'hidden' }}>
                              {category.description_excerpt || category.description}
                            </p>
                          )}
                          {/* <small className="text-muted">{category.post_count} posts</small> */}
                        </Card.Body>
                      </Card>
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default Forum;
